<template>
  <div v-if="globalStore.boxDetailsDrawer && deepBoxCoreStore.selectedBox">
    <DeepInfoPanel
      v-if="isPreviewRouteAndBoxPrivate"
      ref="deepInfoPanelRef"
      v-model="globalStore.boxDetailsDrawer"
      data-test-id="details-sidebar"
      disable-resize-watcher
      disable-route-watcher
      :loading="loading"
      :permanent="!xs"
      resizable
      :temporary="xs"
      touchless
      :width="infoPanelWidth"
    >
      <template #header>
        <v-card-item>
          <template #prepend>
            <v-btn
              density="compact"
              icon="far fa-chevrons-right"
              name="btn-toggle-box-details-drawer"
              variant="text"
              @click="globalStore.boxDetailsDrawer = false"
            ></v-btn>
          </template>
        </v-card-item>
      </template>
      <template #content>
        <BoxDetailsDrawerComments
          v-if="displayItem?.nodeId"
          ref="commentsRef"
          :can-comment="canComment(displayItem as Node) && !isBoxPrivate"
          :generic-folder="currentGenericFolder"
          :node-id="displayItem.nodeId"
          :scroll-el
          @ready="onDocumentsReady"
        />
      </template>
    </DeepInfoPanel>
    <DeepInfoPanel
      v-else
      ref="deepInfoPanelRef"
      v-model="globalStore.boxDetailsDrawer"
      data-test-id="details-sidebar"
      :details="showSectionDetails ? detailsItems : []"
      disable-resize-watcher
      disable-route-watcher
      :loading="loading"
      :permanent="!xs"
      resizable
      :temporary="xs"
      :thumbnail-name="
        displayItemName
          ? displayItemName
          : t('labels.n_elements_selected', nodes.length)
      "
      touchless
      :width="infoPanelWidth"
    >
      <template #thumbnail>
        <v-img
          v-if="nodes.length > 1"
          height="150"
          src="/svg/illustrations/nodes-multiple-selected.svg"
        />
        <BoxDetailsDrawerNodePreview
          v-else
          max-height="180"
          max-width="180"
          :node="displayItem"
        />
      </template>
      <template v-if="displayItem" #content>
        <!-- SECTION TAGS -->
        <template v-if="showSectionTags">
          <v-divider class="my-2" />
          <BoxDetailsDrawerTags
            v-if="displayItem"
            ref="tagsRef"
            :box-id="boxId"
            :node="displayItem as Node"
            :organization-id="organizationId"
            :type-id="typeId"
            @update:tags="
              emit('update:tags', {
                nodeId: displayItem.nodeId ?? '',
                tags: $event.tags,
              })
            "
          />
        </template>
        <!-- /SECTION TAGS -->

        <!-- SECTION DEEP_PAY PAYMENTS HISTORY -->
        <template v-if="showSectionDeepPay">
          <DeepPayPaymentsHistoryCardExpandable
            v-if="deepBoxBoxInfoStore.boxInfo && displayItem.nodeId"
            :box-id="boxId"
            :mime-type="displayItem?.mimeType || ''"
            :node-id="displayItem.nodeId"
            :origin-organization-id="
              deepBoxBoxInfoStore.boxInfo.company.companyId
            "
            :type-id="typeId"
          />
        </template>
        <!-- /SECTION DEEP_PAY PAYMENTS HISTORY -->

        <!-- SECTION COMMENTS -->
        <template v-if="showSectionComments">
          <v-divider class="my-2" />
          <!-- SECTION COMMENTS -->
          <BoxDetailsDrawerComments
            v-if="displayItem.nodeId"
            ref="commentsRef"
            :can-comment="canComment(displayItem as Node) && !isBoxPrivate"
            :generic-folder="currentGenericFolder"
            :node-id="displayItem.nodeId"
            :scroll-el
            @ready="onDocumentsReady"
            @update:size="
              emit('update:comments:size', {
                nodeId: displayItem.nodeId,
                size: $event,
              })
            "
          />
          <!--  /SECTION COMMENTS -->
        </template>
        <!-- /SECTION COMMENTS -->

        <!-- SECTION DeepValidator -->
        <template
          v-if="displayItem && isNodeMimeTypePdf(displayItem.mimeType ?? '')"
        >
          <v-divider class="my-2" />
          <BoxDetailsDrawerDeepValidator
            v-if="displayItem.nodeId"
            :node-id="displayItem.nodeId"
          />
        </template>
        <!-- /SECTION DeepValidator -->
      </template>
    </DeepInfoPanel>
  </div>
</template>
<script lang="ts" setup>
import { useDisplay, useGoTo } from 'vuetify'
import { BOX_DETAILS_DRAWER_SCROLL_IDS } from '@/constants'
import first from 'lodash/first'
import last from 'lodash/last'
import { DeepInfoPanel } from '@deepcloud/deep-ui-lib'
import {
  isNodeImage,
  isNodeMimeTypePdf,
  isNodeTypeFolder,
} from '@deepcloud/deep-ui-utils'
import { formatDateTimeWithoutSeconds } from '@deepcloud/deep-ui-utils/date'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info.ts'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes.ts'
import { useGlobalStore } from '@/stores/global/global'
import { useApi } from '@/composables/api.ts'
import { useBox } from '@/composables/box/box'
import { useBoxBreadcrumbs } from '@/composables/box/box-breadcrumbs'
import { useContextGenericFolders } from '@/composables/context/ctx-generics'
import { useNodePolicy } from '@/composables/node/node-policy'
import { useNodeSection } from '@/composables/node/node-section'
import BoxDetailsDrawerComments from '@/components/box/details-drawer/BoxDetailsDrawerComments.vue'
import BoxDetailsDrawerDeepValidator from '@/components/box/details-drawer/BoxDetailsDrawerDeepValidator.vue'
import BoxDetailsDrawerNodePreview from '@/components/box/details-drawer/BoxDetailsDrawerNodePreview.vue'
import BoxDetailsDrawerTags from '@/components/box/details-drawer/BoxDetailsDrawerTags.vue'
import DeepPayPaymentsHistoryCardExpandable from '@/components/deeppay/paymentshistory/DeepPayPaymentsHistoryCardExpandable.vue'
import type { Node, Path, PathSegment, Tag } from '@deepcloud/api-deep-box'
import type { DeepListKeyValueItem } from '@deepcloud/deep-ui-lib'

export interface DisplayItem extends Partial<Node> {
  thumbnailUrl: string
  parentPath: Path | undefined
}

const props = defineProps({
  hidePreview: {
    type: Boolean,
    default: false,
  },
  organizationId: {
    type: String,
    default: null,
  },
  typeId: {
    type: String,
    required: true,
  },
  boxId: {
    type: String,
    required: true,
  },
  folderNodeId: {
    type: String,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  nodes: {
    type: Array as PropType<Node[]>,
    required: true,
  },
})

const emit = defineEmits<{
  'update:tags': [{ nodeId: string; tags: Tag[] }]
  'update:comments:size': [{ nodeId: string; size: number }]
}>()

const route = useRoute()
const { t } = useI18n()
const { xs, smAndDown } = useDisplay()
const { deepBoxAPI } = useApi()

const deepInfoPanelRef = ref<InstanceType<typeof DeepInfoPanel>>()
const scrollEl = computed(
  () => deepInfoPanelRef.value?.getScrollEl() ?? undefined,
)

const commentsRef = useTemplateRef('commentsRef')
const tagsRef = useTemplateRef('tagsRef')

function scrollTo(el: HTMLElement | undefined) {
  return goTo(el as HTMLElement, {
    container: scrollEl.value,
  })
}

const commentsReadyCallback = shallowRef<() => void>()
async function scrollToSection(section: 'Comments' | 'Tags') {
  if (section === 'Comments') {
    await scrollTo(commentsRef.value?.$el)
  } else if (section === 'Tags') {
    await scrollTo(tagsRef.value?.$el)
  }
}

function onDocumentsReady() {
  commentsReadyCallback.value?.()
  commentsReadyCallback.value = undefined
}

const thumbnailUrl = ref('')

const deepBoxCoreStore = useDeepBoxCoreStore()
const globalStore = useGlobalStore()
const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()

const { isBoxPrivate } = useBox(computed(() => deepBoxCoreStore.selectedBox))

const shouldCommentsFocus = computed(
  () =>
    !!globalStore.boxDetailsDrawerScrollId &&
    globalStore.boxDetailsDrawerScrollId ===
      BOX_DETAILS_DRAWER_SCROLL_IDS.BOX_DETAILS_DRAWER_SCROLL_ID_COMMENTS,
)

const genericFolders = useContextGenericFolders()

const currentGenericFolder = computed(() =>
  genericFolders.value.find((g) => g.nodeId === props.folderNodeId),
)

function isFirstItemRoot(item: PathSegment) {
  return ['inbox', 'files', 'trash'].includes(item.name)
}

const isPreviewRoute = computed(
  () => route.name === 'node-preview' || route.name === 'link-preview',
)

const isPreviewRouteAndBoxPrivate = computed(
  () => isPreviewRoute.value && isBoxPrivate.value,
)

const { boxBreadcrumbsFiltered } = useBoxBreadcrumbs()

const displayItem = computed(() => {
  if (props.nodes.length > 1) return undefined

  const item: DisplayItem = {
    displayName: '',
    type: 'folder',
    thumbnailUrl: thumbnailUrl.value,
    tags: [],
    parentPath: undefined,
  } as DisplayItem

  if (props.nodes.length) {
    Object.assign(item, props.nodes[0])
  } else if (deepBoxDeepBoxesBoxesNodesStore.selectedNodes.length === 0) {
    Object.assign(item, {
      ...last(boxBreadcrumbsFiltered.value),
    })
    if (
      boxBreadcrumbsFiltered.value.length === 1 &&
      isFirstItemRoot(item as PathSegment)
    ) {
      item.displayName = t(`box_details.details_drawer.${item.name}`)
    }
  } else {
    Object.assign(
      item,
      last(deepBoxDeepBoxesBoxesNodesStore.selectedNodes as Node[]),
    )
  }

  return item
})

const { getNodeSectionTranslation } = useNodeSection()
const displayItemName = computed(() =>
  displayItem.value && displayItem.value.displayName
    ? getNodeSectionTranslation(displayItem.value.displayName)
    : undefined,
)

const nodeLocation = computed(() => {
  const firstBoxDetailsBreadCrumbsItem = first(boxBreadcrumbsFiltered.value)
  if (!firstBoxDetailsBreadCrumbsItem) return null

  if (firstBoxDetailsBreadCrumbsItem?.item) {
    if (
      isFirstItemRoot(firstBoxDetailsBreadCrumbsItem as unknown as PathSegment)
    ) {
      return t(
        `box_details.details_drawer.${firstBoxDetailsBreadCrumbsItem.item.name}`,
      )
    } else {
      return firstBoxDetailsBreadCrumbsItem.item.displayName
    }
  }
  if (displayItem.value?.parentPath) {
    const firstParentPathSegmentItemName = first(
      displayItem.value?.parentPath?.segments,
    )?.name
    if (firstParentPathSegmentItemName) {
      return t(`box_details.details_drawer.${firstParentPathSegmentItemName}`)
    }
  }
  return null
})

const detailsItems = computed<DeepListKeyValueItem[]>(() =>
  [
    {
      show: () => !!displayItem.value?.type,
      key: t('box_details.details_drawer.category'),
      value: t(`box_details.details_drawer.${displayItem.value?.type}`) ?? '',
    },
    {
      show: () => !!nodeLocation.value,
      key: t('box_details.details_drawer.location'),
      value: getNodeSectionTranslation(nodeLocation.value ?? '') ?? '',
    },
    {
      show: () => !!displayItem.value?.ownerUser?.displayName,
      key: t('box_details.details_drawer.owner'),
      value: displayItem.value?.ownerUser?.me
        ? t('box_details.details_drawer.me')
        : (displayItem.value?.ownerUser?.displayName ?? ''),
    },
    {
      show: () => !!displayItem.value?.modified?.time,
      key: t('box_details.details_drawer.modified'),
      value: displayItem.value?.modified?.time
        ? formatDateTimeWithoutSeconds(displayItem.value.modified.time)
        : '',
    },
    {
      show: () => !!displayItem.value?.created?.time,
      key: t('box_details.details_drawer.created'),
      value: displayItem.value?.created?.time
        ? formatDateTimeWithoutSeconds(displayItem.value.created.time)
        : '',
    },
  ]
    .filter((i) => i.show?.() ?? true)
    .map((d) => ({
      key: d.key,
      value: d.value ?? '',
    })),
)

const displayItemNodeId = computed(() => displayItem.value?.nodeId)

const isDisplayItemTypeFolder = computed(
  () => displayItem.value?.type === 'folder',
)

const { canComment, canTag } = useNodePolicy()

const showSectionComments = computed(
  () =>
    !isDisplayItemTypeFolder.value &&
    (canComment(displayItem.value as Node) || isBoxPrivate.value),
)

async function fetchDetailsData() {
  if (
    displayItem.value &&
    displayItem.value.nodeId &&
    displayItem.value.type &&
    !isNodeTypeFolder(displayItem.value.type)
  ) {
    try {
      const res = await deepBoxAPI.value.nodesThumbnailUrl.get(
        displayItem.value.nodeId,
      )
      if (res.data) {
        thumbnailUrl.value = res.data
      }
    } catch (e) {
      console.log(e)
    }
  }
}

const goTo = useGoTo()

watch(
  () => displayItemNodeId.value,
  () => {
    if (displayItem.value) {
      if (
        displayItem.value.mimeType &&
        (isNodeImage({ mimeType: displayItem.value.mimeType }) ||
          isNodeMimeTypePdf(displayItem.value.mimeType))
      ) {
        displayItem.value.thumbnailUrl = thumbnailUrl.value
      } else {
        displayItem.value.thumbnailUrl = ''
      }

      if (shouldCommentsFocus.value) {
        if (displayItem.value?.commentCount === 0) {
          nextTick(() => {
            commentsRef.value?.addFirstComment()
          })
        } else {
          commentsReadyCallback.value = () => {
            scrollToSection('Comments')
          }
        }
      }
    }
    if (globalStore.boxDetailsDrawer) {
      fetchDetailsData()
    }
  },
  { immediate: true },
)

watch(
  () => globalStore.boxDetailsDrawer,
  (newValue) => {
    if (newValue) {
      fetchDetailsData()
    }
    if (displayItem.value) {
      if (
        displayItem.value.mimeType &&
        (isNodeImage({ mimeType: displayItem.value.mimeType }) ||
          isNodeMimeTypePdf(displayItem.value.mimeType))
      ) {
        displayItem.value.thumbnailUrl = thumbnailUrl.value
      } else {
        displayItem.value.thumbnailUrl = ''
      }
    }
  },
)

const infoPanelWidth = computed(() => {
  if (isPreviewRouteAndBoxPrivate.value) {
    return 256
  }
  return smAndDown.value ? window.innerWidth : 320
})

const showSectionDetails = computed(
  () => props.nodes.length === 1 && !isBoxPrivate.value,
)

const showSectionTags = computed(
  () => displayItemNodeId.value && canTag(displayItem.value as Node),
)

const showSectionDeepPay = computed(
  () =>
    deepBoxBoxInfoStore.boxInfo?.company?.companyId &&
    displayItemNodeId.value &&
    !isBoxPrivate.value,
)
</script>
